// TextMessagingSections.tsx · v12.10.27 (#2146 rates line Q21a, most AI texts Q28, picture reply Q27; #2152 the page says what ships) · 2026-10-31 · VoiceSpectrum AI
// TextMessagingSections.tsx · v12.10.26 (#T154 Settings › Text Messaging - the new main area, UI-PROTO-T154 v1.0 screen 2; Q8 Q10 Q11 Q12 Q15 Q17 Q19 TC1) · 2026-10-30 · VoiceSpectrum AI
// Two section cards, each with Discard / Save section: Text messaging workflow (On / Off, new practice Off; close after N hours with no
// reply, default 24) and Automatic reply when the workflow is off (EN / ES / ZH, default "Please call our office at {organization.phone}.").
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { saveErrorToast } from '../lib/saveError';
import { toast } from '../lib/toast';
import TextLangEditor from '../components/texting/TextLangEditor';
import '../components/texting/texting.css';

interface TextMessagingData {
  workflowOn: boolean; closeAfterHours: number; autoReply: Record<string, string>; defaultAutoReply: Record<string, string>; officePhone?: string | null;
  // v12.10.27 (#2146 Q21a Q27 Q28)
  ratesLine?: Record<string, string>; defaultRatesLine?: Record<string, string>;
  pictureReply?: Record<string, string>; defaultPictureReply?: Record<string, string>;
  maxAiTexts?: number; maxAiTextsChoices?: number[];
}
type WorkflowDraft = { workflowOn: boolean; closeAfterHours: number; maxAiTexts: number; ratesLine: Record<string, string>; pictureReply: Record<string, string> };
export type TextingSectionKey = 'workflow' | 'autoReply';
export interface TextMessagingState { dirty: Record<TextingSectionKey, boolean>; anyDirty: boolean; saveAll: () => void; discardAll: () => void }

const QKEY = ['t2-text-messaging'];

export default function TextMessagingSections({ onStateChange }: { onStateChange?: (s: TextMessagingState) => void } = {}) {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery<TextMessagingData>({ queryKey: QKEY, queryFn: () => api.get<TextMessagingData>('/t2/settings/text-messaging') });
  const [wfSaved, setWfSaved] = useState<WorkflowDraft | null>(null);
  const [wf, setWf] = useState<WorkflowDraft | null>(null);
  const [arSaved, setArSaved] = useState<Record<string, string> | null>(null);
  const [ar, setAr] = useState<Record<string, string> | null>(null);
  useEffect(() => {
    if (!data) return;
    const w = { workflowOn: data.workflowOn, closeAfterHours: data.closeAfterHours, maxAiTexts: data.maxAiTexts ?? 30,
      ratesLine: { ...(data.ratesLine ?? {}) }, pictureReply: { ...(data.pictureReply ?? {}) } };   // v12.10.27 (#2146)
    setWfSaved(w); setWf({ ...w }); setArSaved({ ...data.autoReply }); setAr({ ...data.autoReply });
  }, [data]);

  const after = () => { qc.invalidateQueries({ queryKey: QKEY }); qc.invalidateQueries({ queryKey: ['t2-texting-status'] }); };
  const saveWf = useMutation({
    mutationFn: (d: WorkflowDraft) => api.put('/t2/settings/text-messaging/workflow', d),
    onSuccess: () => { setWfSaved(wf ? { ...wf } : wfSaved); after(); toast('success', 'Text messaging workflow saved'); },
    onError: (e) => saveErrorToast(e, 'the Text messaging workflow'),
  });
  const saveAr = useMutation({
    mutationFn: (d: Record<string, string>) => api.put('/t2/settings/text-messaging/auto-reply', { autoReply: d }),
    onSuccess: () => { setArSaved(ar ? { ...ar } : arSaved); after(); toast('success', 'Automatic reply saved'); },
    onError: (e) => saveErrorToast(e, 'the automatic reply'),
  });

  const wfDirty = !!wf && !!wfSaved && JSON.stringify(wf) !== JSON.stringify(wfSaved);
  const arDirty = !!ar && !!arSaved && JSON.stringify(ar) !== JSON.stringify(arSaved);
  const report = useRef(onStateChange); report.current = onStateChange;
  useEffect(() => {
    report.current?.({
      dirty: { workflow: wfDirty, autoReply: arDirty }, anyDirty: wfDirty || arDirty,
      saveAll: async () => {
        try { if (wfDirty && wf) await saveWf.mutateAsync(wf); if (arDirty && ar) await saveAr.mutateAsync(ar); } catch { /* toast */ }
      },
      discardAll: () => { if (wfSaved) setWf({ ...wfSaved }); if (arSaved) setAr({ ...arSaved }); },
    });
  }, [wfDirty, arDirty, wf, ar, wfSaved, arSaved]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading || !data || !wf || !wfSaved || !ar || !arSaved) return <div className="settings-loading">Loading Text Messaging settings…</div>;
  if (error) return <div className="settings-error">Text Messaging settings could not be loaded.</div>;

  return (
    <div className="organization-panel texting-panel">
      <Section title="Text messaging workflow" dirty={wfDirty} saving={saveWf.isPending}
        description="Runs the practice’s workflow over text: the first text is the Disclosure, then one text per card; the patient’s reply is the answer (no read-back by text). The AI finishes the conversation, then staff carry on in the same chat. Turns on the Text messages tab in Messages. Separate from Text replies (Call Handling › Texts on calls)."
        onSave={() => saveWf.mutate(wf)} onDiscard={() => setWf({ ...wfSaved })}>
        <div className="tx-row">
          <div className="tx-row-main">
            <b>Text messaging workflow</b><span className="tx-default">New practice default: Off</span>
            <span className="tx-row-sub">{wf.workflowOn
              ? 'On: a patient who texts the practice first is answered by the practice’s workflow, one text per card, and the conversation is a card under Messages › Text messages - the same card as a call. When the AI has finished, staff reply in the same chat.'
              : 'Off: Messages › Text Messages stays “Not available yet” and a patient who texts first gets the automatic reply below. The text cards in the practice’s workflows also start disabled.'}</span>
          </div>
          <div className="seg-group" role="radiogroup" aria-label="Text messaging workflow">
            <button type="button" className={`seg${!wf.workflowOn ? ' on' : ''}`} onClick={() => setWf({ ...wf, workflowOn: false })}><span className="seg-dot" />Off</button>
            <button type="button" className={`seg${wf.workflowOn ? ' on' : ''}`} onClick={() => setWf({ ...wf, workflowOn: true })}><span className="seg-dot" />On</button>
          </div>
        </div>
        {/* v12.10.27 (#2146 Q21a, Tushar 10/31): the line after the Disclosure on the first text - the Disclosure itself is the workflow's
            card, worded with {conversation.channel}. */}
        <div className="tx-row">
          <div className="tx-row-main">
            <b>Rates line on the first text</b>
            <span className="tx-row-sub">Added after the Disclosure on the first text of a conversation, before “Reply STOP to opt out, HELP for help.”</span>
            <TextLangEditor value={wf.ratesLine} defaults={data.defaultRatesLine ?? {}} officePhone={data.officePhone} onChange={(v) => setWf({ ...wf, ratesLine: v })} />
          </div>
        </div>
        {/* v12.10.27 (#2146 Q28, Tushar 10/31 "configurable - unlimited, 15, 30 etc.."): the most AI texts in one conversation. */}
        <div className="tx-row">
          <div className="tx-row-main">
            <b>Most AI texts in one conversation</b>
            <span className="tx-row-sub">Then the patient gets “Thank you. Our staff will text you back.” and the card waits for staff.</span>
          </div>
          <div className="seg-group" role="radiogroup" aria-label="Most AI texts">
            {(data.maxAiTextsChoices ?? [0, 15, 30, 50]).map((n) => (
              <button key={n} type="button" className={`seg${wf.maxAiTexts === n ? ' on' : ''}`} onClick={() => setWf({ ...wf, maxAiTexts: n })}>
                <span className="seg-dot" />{n === 0 ? 'Unlimited' : n}
              </button>
            ))}
          </div>
        </div>
        {/* v12.10.27 (#2146 Q27): pictures are not received in this build. */}
        <div className="tx-row">
          <div className="tx-row-main">
            <b>Reply to a picture</b>
            <span className="tx-row-sub">Pictures (MMS) are not received - the patient gets this text.</span>
            <TextLangEditor value={wf.pictureReply} defaults={data.defaultPictureReply ?? {}} officePhone={data.officePhone} onChange={(v) => setWf({ ...wf, pictureReply: v })} />
          </div>
        </div>
        <div className="tx-row">
          <div className="tx-row-main">
            <b>Close a conversation after no reply</b>
            <span className="tx-row-sub">If the patient stops replying, the conversation closes with what was collected - like a caller who hangs up mid-way. The card stays pending for staff. A later text continues where it left off (repeat-caller rules).</span>
          </div>
          <div className="tx-num">
            <input type="number" min={1} max={168} value={wf.closeAfterHours} aria-label="Close after hours"
              onChange={(e) => setWf({ ...wf, closeAfterHours: Math.max(1, Math.min(168, Number(e.target.value) || 24)) })} />
            <span>hours</span><span className="tx-default">Default: 24</span>
          </div>
        </div>
      </Section>

      <Section title="Automatic reply when the workflow is off" dirty={arDirty} saving={saveAr.isPending}
        description="Sent to a text that starts a new conversation - a patient who texts first, or a reply after the reply window - while the Text messaging workflow is Off, whatever Text replies is set to. A different text from the one in Texts on calls."
        onSave={() => saveAr.mutate(ar)} onDiscard={() => setAr({ ...arSaved })}>
        <div className={`tx-row${arDirty ? ' tx-amber' : ''}`}>
          <div className="tx-row-main">
            <b>Automatic reply</b>{wf.workflowOn ? <span className="tx-default">not in use - the workflow is On</span> : <span className="tx-inuse">IN USE</span>}
            <TextLangEditor value={ar} defaults={data.defaultAutoReply} officePhone={data.officePhone} onChange={setAr} />
            <span className="tx-row-sub">Sent once, automatically (at most once a day to the same number).</span>
          </div>
        </div>
        <div className="tx-row">
          <div className="tx-row-main">
            <b>Language of automatic replies</b>
            <span className="tx-row-sub">The first that applies: the language used on the call, when the text is a reply to a text sent after a call; the contact card’s Preferred language (one contact on that phone, field filled); the practice’s default language. Two linked contacts with different preferred languages → default language.</span>
          </div>
        </div>
        <p className="tx-rights">Rights: Settings › Text Messaging - View + Edit (admin groups by default). Messages › Text Messages has its own View right, like Phone calls. Every settings change and every automatic reply sent is audited.</p>
      </Section>
    </div>
  );
}

function Section(props: { title: string; description: string; dirty: boolean; saving: boolean; onSave: () => void; onDiscard: () => void; children: ReactNode }) {
  return (
    <section className={`settings-section-card${props.dirty ? ' dirty' : ''}`}>
      <header className="settings-section-header">
        <div className="settings-section-headrow">
          <h2>{props.title}</h2>
          <div className="settings-actions">
            <button type="button" className="settings-secondary" disabled={!props.dirty || props.saving} onClick={props.onDiscard}>Discard</button>
            <button type="button" className="settings-primary" disabled={!props.dirty || props.saving} onClick={props.onSave}>{props.saving ? 'Saving…' : 'Save section'}</button>
          </div>
        </div>
        <p className="ch-sec-desc">{props.description}</p>
      </header>
      <div className="ch-section-body">{props.children}</div>
    </section>
  );
}
